import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import cms from 'public/static/locales/de/cms.json';
import common from 'public/static/locales/de/common.json';
import form from 'public/static/locales/de/form.json';
import validation from 'public/static/locales/de/validation.json';

/**
 * Translations for a club website: German, and only what the page asks for.
 *
 * The app's `i18n` module bundles every namespace of every language — 3 MB of JSON,
 * about 670 KB over the wire — and a club website reads a few dozen strings of it. The
 * app router never switches language (only `pages/_app` does), so a club site is German
 * throughout; the four namespaces nearly every page uses come with the bundle, and any
 * other one is fetched the moment a component asks for it — the member application form
 * loads `member-management`, the chatbot `ai` — as a chunk of its own.
 *
 * Nothing here has to know which widgets exist: a namespace nobody listed still
 * arrives, on first use. The server renders with the same instance, suspending on a
 * namespace exactly as the browser does, so both sides hydrate to the same text.
 */
const namespaceLoader: BackendModule = {
    type: 'backend',
    init() {},
    read(language, namespace, callback) {
        import(`../public/static/locales/${language}/${namespace}.json`).then(
            (module) => callback(null, module.default ?? module),
            (error) => callback(error, false)
        );
    }
};

// The app's module may already have run — a visitor who came from the app keeps its
// full set — and initialising the shared instance twice would throw that set away.
if (!i18n.isInitialized) {
    i18n.use(namespaceLoader)
        .use(initReactI18next)
        .init({
            lng: 'de',
            fallbackLng: 'de',
            resources: { de: { cms, common, form, validation } },
            ns: ['cms', 'common', 'form', 'validation'],
            // Bundled and fetched side by side: the four above are in the bundle,
            // everything else comes through the loader.
            partialBundledLanguages: true,
            // Synchronous for what is bundled, so the first render has its text.
            initAsync: false,
            interpolation: { escapeValue: false }
        });
} else {
    // Already running: the app got there first, or (in development) this module was
    // re-evaluated after a locale file changed. The instance survives either way, so
    // the bundled namespaces are handed over again — a key added to cms.json otherwise
    // showed as its raw name ("publicSite.…") until the server restarted.
    Object.entries({ cms, common, form, validation }).forEach(([namespace, bundle]) =>
        i18n.addResourceBundle('de', namespace, bundle, true, true)
    );
}

export default i18n;
